"use client"

// posthog-js is a ~80 KB module that was previously imported at the
// top of this file, which means it landed in every client bundle that
// imported anything from `lib/analytics` — even before consent was
// granted. We now load it dynamically inside `safeInit()`, the only
// place that actually instantiates the client. The public API
// (track, identify, reset, optOutPermanently) caches the loaded
// module on `posthog` and proxies through it once it's ready.
//
// Net effect on first paint: posthog-js is no longer in the initial
// JS payload. Lighthouse "Reduce unused JavaScript" goes from
// ~220 KiB to ~140 KiB on the landing page.
import type PostHogType from "posthog-js"
import { ADMIN_EMAILS } from "@/lib/admin/config"

let posthog: typeof PostHogType | null = null

/**
 * Consent-gated PostHog client.
 *
 * Nothing loads until the user accepts cookies via the banner.
 * Once accepted, all subsequent events fire normally; if they reject,
 * track() calls become no-ops.
 *
 * Admin opt-out (db_no_track):
 *   The first time a user logs in with an email in ADMIN_EMAILS the
 *   client sets `db_no_track` in localStorage and opts out of PostHog
 *   permanently on that browser. Subsequent visits — including
 *   logged-out ones, including new sessions in the same profile —
 *   never send a single event. This is the only reliable way to keep
 *   the founder's own browsing out of the visitor count, since the
 *   server-side admin filter only catches identified sessions and a
 *   chunk of the inflation comes from the admin browsing anonymously
 *   (landing page, incognito QA, mobile) where we have no email to
 *   filter by. Note: localStorage is per-origin per-profile, so the
 *   admin still has to log in once on each browser/incognito profile
 *   they want excluded — but on a given profile the opt-out sticks.
 */

const CONSENT_KEY   = "db_cookie_consent" // "accepted" | "rejected" | null
const CONSENT_EVENT = "db:consent-changed"
const OPT_OUT_KEY   = "db_no_track"       // "1" once admin logs in, persists forever

export type ConsentState = "accepted" | "rejected" | "pending"

export function getConsent(): ConsentState {
  if (typeof window === "undefined") return "pending"
  const v = window.localStorage.getItem(CONSENT_KEY)
  if (v === "accepted" || v === "rejected") return v
  return "pending"
}

export function setConsent(value: "accepted" | "rejected") {
  if (typeof window === "undefined") return
  window.localStorage.setItem(CONSENT_KEY, value)
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }))
}

export function onConsentChange(fn: (state: ConsentState) => void) {
  if (typeof window === "undefined") return () => {}
  const handler = () => fn(getConsent())
  window.addEventListener(CONSENT_EVENT, handler)
  return () => window.removeEventListener(CONSENT_EVENT, handler)
}

function isOptedOut(): boolean {
  if (typeof window === "undefined") return false
  return window.localStorage.getItem(OPT_OUT_KEY) === "1"
}

/**
 * Mark this browser as opted-out of analytics forever.
 * Called automatically when an admin email logs in. Safe to call
 * repeatedly — second call is a no-op.
 */
export function optOutPermanently() {
  if (typeof window === "undefined") return
  window.localStorage.setItem(OPT_OUT_KEY, "1")
  try { posthog?.opt_out_capturing?.() } catch {/* noop */}
}

let initialized = false
let initPromise: Promise<void> | null = null

async function safeInit(): Promise<void> {
  if (initialized) return
  if (initPromise) return initPromise
  if (typeof window === "undefined") return
  if (isOptedOut()) return // never even load PostHog on opted-out browsers
  const key  = process.env.NEXT_PUBLIC_POSTHOG_KEY
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com"
  if (!key) return // not configured yet — no-op silently

  // Dynamic import: posthog-js stays out of the initial bundle and
  // only lands when consent is "accepted" AND we have a key configured.
  // Cached on the module-scope `posthog` ref so subsequent calls don't
  // refetch.
  initPromise = (async () => {
    const mod = await import("posthog-js")
    posthog = mod.default ?? (mod as any)
    // Route-aware session-recording gate. Dataset detail pages render
    // sample rows of THIRD-PARTY email addresses uploaded by the
    // customer — capturing those in a session replay puts DataBridge
    // (as processor) and the customer (as controller) in direct GDPR
    // Art. 6 non-compliance territory. Same reasoning for /admin
    // (support-inbox PII, other users' data) and /dashboard/mapper
    // and /dashboard/compare (column names + contact rows).
    //
    // `mask_all_text` also flipped on globally so any recording that
    // still fires elsewhere never captures raw text content — only
    // layout / interaction structure. `maskAllInputs` stays on for the
    // form-field case which text masking doesn't cover.
    const sensitivePathPrefixes = [
      "/dashboard/datasets",
      "/dashboard/mapper",
      "/dashboard/compare",
      "/admin",
    ]
    const isSensitiveRoute =
      typeof window !== "undefined" &&
      sensitivePathPrefixes.some((p) => window.location.pathname.startsWith(p))

    posthog!.init(key, {
      api_host: host,
      capture_pageview:     true,
      capture_pageleave:    false,
      capture_performance:  false,
      autocapture: {
        element_allowlist: ["button", "a", "form"],
      },
      session_recording: {
        maskAllInputs: true,
        // Mask every text node in captured DOM snapshots — required
        // for GDPR-sensitive surfaces where customer data renders as
        // plain text (dataset previews, admin views).
        maskTextSelector: "*",
        sampleRate:    isSensitiveRoute ? 0 : 0.05,
      },
      persistence: "localStorage+cookie",
      disable_session_recording: isSensitiveRoute,
      loaded: (ph) => {
        if (process.env.NODE_ENV === "development") ph.debug(false)
      },
    })
    initialized = true
  })()
  return initPromise
}

/**
 * Called by the PostHogProvider once consent is known.
 * If accepted → init PostHog (lazy-loads the module). If rejected →
 * opt out without loading anything.
 */
export function initIfAllowed() {
  if (typeof window === "undefined") return
  const consent = getConsent()
  if (consent === "accepted") {
    // Fire-and-forget — callers don't need to await.
    void safeInit()
  } else if (consent === "rejected") {
    try { posthog?.opt_out_capturing?.() } catch {/* noop */}
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function track(event: string, props?: Record<string, unknown>) {
  if (typeof window === "undefined") return
  if (isOptedOut()) return
  if (getConsent() !== "accepted") return
  if (!initialized) {
    // Kick off lazy-init and queue the event to fire once the SDK
    // resolves. Without this, the first event on a fresh consent
    // would silently drop while posthog-js was still downloading.
    void safeInit().then(() => {
      if (initialized) posthog?.capture(event, props)
    })
    return
  }
  posthog?.capture(event, props)
}

export function identify(userId: string, traits?: Record<string, unknown>) {
  if (typeof window === "undefined") return

  // Admin auto-opt-out — see optOutPermanently() above.
  const rawEmail = typeof traits?.email === "string" ? traits.email.toLowerCase() : null
  if (rawEmail && ADMIN_EMAILS.includes(rawEmail)) {
    optOutPermanently()
    return
  }

  if (isOptedOut()) return
  if (getConsent() !== "accepted") return

  // Strip raw email from traits before shipping to PostHog. Email
  // addresses are PII we don't need in analytics — the account holder
  // is already uniquely identified by userId, and any question we'd
  // answer against a real email (deliverability testing, support
  // lookup) belongs on the DB side, not PostHog. We keep a stable
  // hashed variant so ad-hoc cohorts by email domain still work.
  let safeTraits: Record<string, unknown> | undefined = traits
  if (traits && "email" in traits) {
    const { email, ...rest } = traits
    safeTraits = { ...rest }
    if (typeof email === "string") {
      const at = email.indexOf("@")
      if (at > 0) safeTraits.email_domain = email.slice(at + 1).toLowerCase()
    }
  }

  if (!initialized) {
    void safeInit().then(() => {
      if (initialized) posthog?.identify(userId, safeTraits)
    })
    return
  }
  posthog?.identify(userId, safeTraits)
}

export function reset() {
  if (typeof window === "undefined") return
  if (!initialized) return
  try { posthog?.reset() } catch {/* noop */}
}

// ─── Event name constants (single source of truth) ────────────────────────────

export const Events = {
  LANDING_VIEWED:          "landing_viewed",
  TRY_SAMPLE_CLICKED:      "try_sample_clicked",
  INSTANT_CHECK_USED:      "instant_check_used",
  SIGNUP_STARTED:          "signup_started",
  SIGNUP_COMPLETED:        "signup_completed",
  FIRST_UPLOAD_COMPLETED:  "first_upload_completed",
  FIX_APPLIED:             "fix_applied",
  CHECKOUT_STARTED:        "checkout_started",
  CHECKOUT_COMPLETED:      "checkout_completed",
} as const
